import { usePathname, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { api } from 'trpc/react';

type TooltipType = 'homepage' | 'list' | 'category' | 'product' | 'other';

export const useTooltip = (authorized: boolean) => {
    const pathname = usePathname() ?? '/';
    const searchParams = useSearchParams();

    const route = useMemo(() => {
        if (pathname === '/') return { type: 'homepage' as const };

        if (pathname.startsWith('/lists/')) {
            const slug = pathname.slice('/lists/'.length);
            return { type: 'list' as const, slug };
        }

        if (pathname.startsWith('/products/')) {
            const slug = pathname.slice('/products/'.length);
            return { type: 'product' as const, slug };
        }

        if (pathname.startsWith('/products')) {
            const category = searchParams.get('category') ?? undefined;
            return { type: 'category' as const, slug: category };
        }

        return { type: 'other' as const };
    }, [pathname, searchParams]);

    const { data: category } = api.category.getBySlug.useQuery(
        route.type === 'category' && route.slug ? route.slug : '',
        {
            select: d => d.item,
            enabled: authorized && route.type === 'category' && !!route.slug,
        },
    );

    const { data: list } = api.list.getBySlug.useQuery(route.type === 'list' ? route.slug : '', {
        enabled: authorized && route.type === 'list',
    });

    const { data: product } = api.product.getBySlug.useQuery(route.type === 'product' ? route.slug : '', {
        enabled: authorized && route.type === 'product',
    });

    return { type: route.type as TooltipType, category, list, product };
};
